import { getToken, clearToken } from '../utils/authToken';
/**
 * ImageGeneratorModal - Version with external open control
 * Wraps ImageGenerator functionality with isOpen/onClose props
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Image, Loader2, Download, X, Sparkles, Copy, MessageSquare, Check, RefreshCw, Maximize2, Save, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

import { API_URL as API } from '../config/api';

// Fullscreen Image Viewer Component (inline for this modal)
const FullscreenViewer = ({ imageBase64, prompt, onClose, onSave, t }) => {
  const [zoom, setZoom] = useState(1);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 3));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.5));
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleDownload = () => {
    try {
      // Convertir base64 a bytes binarios
      const byteCharacters = atob(imageBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Crear Blob binario real
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Crear URL del blob y descargar
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `damyxia_imagen_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(t.imageDownloaded);
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Error al descargar');
    }
  };

  const handleCopy = async () => {
    try {
      const response = await fetch(`data:image/png;base64,${imageBase64}`);
      const blob = await response.blob();
      if (navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        setCopied(true);
        toast.success(t.imageCopied);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
      await navigator.clipboard.writeText(`data:image/png;base64,${imageBase64}`);
      setCopied(true);
      toast.success(t.urlCopied);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(t.copyError);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] bg-black/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-200" data-testid="fullscreen-image-viewer">
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{t.fullscreenView || 'Vista Completa'}</h3>
            <p className="text-xs text-zinc-400 truncate max-w-[300px] md:max-w-[500px]">{prompt}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors group" data-testid="fullscreen-close-btn">
          <X className="w-6 h-6 text-zinc-400 group-hover:text-white" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-auto p-4">
        <img src={`data:image/png;base64,${imageBase64}`} alt={t.generatedImage} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-transform duration-200" style={{ transform: `scale(${zoom})` }} draggable={false} data-testid="fullscreen-image" />
      </div>
      <div className="flex items-center justify-center gap-2 px-4 py-4 bg-zinc-900/80 border-t border-zinc-800">
        <div className="flex items-center gap-1 px-3 py-2 bg-zinc-800 rounded-xl border border-zinc-700">
          <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"><ZoomOut className="w-5 h-5 text-zinc-300" /></button>
          <span className="px-3 text-sm text-zinc-300 font-medium min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(z + 0.25, 3))} className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"><ZoomIn className="w-5 h-5 text-zinc-300" /></button>
        </div>
        <button onClick={handleCopy} className="flex items-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl border border-zinc-700 transition-colors">
          {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5 text-zinc-300" />}
          <span className="text-sm font-medium text-zinc-200">{copied ? t.copied : t.copy}</span>
        </button>
        <button onClick={handleDownload} className="flex items-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 rounded-xl transition-colors">
          <Download className="w-5 h-5 text-white" />
          <span className="text-sm font-medium text-white">{t.download}</span>
        </button>
        {onSave && (
          <button onClick={() => { onSave({ base64: imageBase64, prompt }); setSaved(true); toast.success(t.imageSaved || 'Guardada'); setTimeout(() => setSaved(false), 2000); }} className="flex items-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl transition-colors">
            {saved ? <Check className="w-5 h-5 text-white" /> : <Save className="w-5 h-5 text-white" />}
            <span className="text-sm font-medium text-white">{saved ? (t.saved || 'Guardada') : (t.save || 'Guardar')}</span>
          </button>
        )}
      </div>
      <div className="absolute bottom-20 left-4 text-xs text-zinc-500"><span>ESC: Cerrar | +/-: Zoom</span></div>
    </div>,
    document.body
  );
};

const ImageGeneratorModal = ({ isOpen, onClose, conversationId, onImageGenerated, onSendToChat, onSaveImage }) => {
  const { language } = useLanguage();
  const [prompt, setPrompt] = useState('');
  const [size, setSize] = useState('1024x1024');
  const [style, setStyle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [lastPrompt, setLastPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [softWarning, setSoftWarning] = useState(''); // CUADRO NEGRO: aviso suave de moderación

  const txt = {
    es: {
      title: 'Generar Imagen',
      poweredBy: 'OpenAI GPT Image 1',
      describeImage: 'Describe la imagen:',
      placeholder: 'Ej: Un gato naranja jugando en un jardin...',
      size: 'Tamanio:',
      style: 'Estilo:',
      generating: 'Generando...',
      createImage: 'CREAR IMAGEN',
      copy: 'Copiar',
      copied: 'Copiada',
      download: 'Descargar',
      expand: 'Pantalla Completa',
      variation: 'Variacion',
      analyze: 'Analizar',
      imageGenerated: 'Imagen generada!',
      imageDownloaded: 'Imagen descargada',
      imageCopied: 'Imagen copiada al portapapeles',
      urlCopied: 'URL de imagen copiada',
      copyError: 'No se pudo copiar la imagen',
      sentToChat: 'Imagen enviada al chat',
      describeError: 'Escribe una descripcion para la imagen',
      generateError: 'Error al generar imagen',
      generatedImage: 'Imagen generada',
      fullscreenView: 'Vista Completa',
      save: 'Guardar',
      saved: 'Guardada',
      imageSaved: 'Imagen guardada',
      sizeSquare: 'Cuadrada (1024x1024)',
      sizeHorizontal: 'Horizontal (1792x1024)',
      sizeVertical: 'Vertical (1024x1792)',
      styleNatural: 'Natural',
      styleOil: 'Pintura al oleo',
      styleAnime: 'Anime',
      stylePhoto: 'Fotorrealista',
      styleWatercolor: 'Acuarela',
      stylePixel: 'Pixel Art',
      styleSketch: 'Boceto',
      style3D: '3D Render',
      styleMinimal: 'Minimalista',
      styleCyber: 'Cyberpunk',
    },
    en: {
      title: 'Generate Image',
      poweredBy: 'OpenAI GPT Image 1',
      describeImage: 'Describe the image:',
      placeholder: 'E.g.: An orange cat playing in a garden...',
      size: 'Size:',
      style: 'Style:',
      generating: 'Generating...',
      createImage: 'CREATE IMAGE',
      copy: 'Copy',
      copied: 'Copied',
      download: 'Download',
      expand: 'Fullscreen',
      variation: 'Variation',
      analyze: 'Analyze',
      imageGenerated: 'Image generated!',
      imageDownloaded: 'Image downloaded',
      imageCopied: 'Image copied to clipboard',
      urlCopied: 'Image URL copied',
      copyError: 'Could not copy image',
      sentToChat: 'Image sent to chat',
      describeError: 'Enter a description for the image',
      generateError: 'Error generating image',
      generatedImage: 'Generated image',
      fullscreenView: 'Fullscreen View',
      save: 'Save',
      saved: 'Saved',
      imageSaved: 'Image saved',
      sizeSquare: 'Square (1024x1024)',
      sizeHorizontal: 'Landscape (1792x1024)',
      sizeVertical: 'Portrait (1024x1792)',
      styleNatural: 'Natural',
      styleOil: 'Oil Painting',
      styleAnime: 'Anime',
      stylePhoto: 'Photorealistic',
      styleWatercolor: 'Watercolor',
      stylePixel: 'Pixel Art',
      styleSketch: 'Sketch',
      style3D: '3D Render',
      styleMinimal: 'Minimalist',
      styleCyber: 'Cyberpunk',
    }
  };

  const t = txt[language] || txt.es;

  const sizes = [
    { value: '1024x1024', label: t.sizeSquare },
    { value: '1792x1024', label: t.sizeHorizontal },
    { value: '1024x1792', label: t.sizeVertical },
  ];

  const styles = [
    { value: '', label: t.styleNatural },
    { value: 'oil painting style', label: t.styleOil },
    { value: 'anime style', label: t.styleAnime },
    { value: 'photorealistic', label: t.stylePhoto },
    { value: 'watercolor style', label: t.styleWatercolor },
    { value: 'pixel art style', label: t.stylePixel },
    { value: 'pencil sketch style', label: t.styleSketch },
    { value: '3D render style', label: t.style3D },
    { value: 'minimalist style', label: t.styleMinimal },
    { value: 'cyberpunk style', label: t.styleCyber },
  ];

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setGeneratedImage(null);
      setPrompt('');
      setLastPrompt('');
      setCopied(false);
      setFullscreenImage(null);
      setSoftWarning('');
    }
  }, [isOpen]);

  const handleGenerate = async (variation = false) => {
    const currentPrompt = variation && lastPrompt ? lastPrompt : prompt;
    if (!currentPrompt.trim()) {
      toast.error(t.describeError);
      return;
    }

    setIsGenerating(true);
    setSoftWarning('');
    try {
      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      
      const fullPrompt = style ? `${currentPrompt}, ${style}` : currentPrompt;
      
      const response = await axios.post(`${API}/generate-image`, {
        prompt: fullPrompt,
        size: size,
        conversation_id: conversationId
      }, { headers });

      // CUADRO NEGRO: si el backend devuelve status="blocked", mostrar aviso suave (no toast rojo)
      if (response.data && response.data.status === 'blocked') {
        setSoftWarning(
          response.data.message ||
          (language === 'es'
            ? 'Algunas palabras pueden activar filtros de seguridad. Intenta ajustar tu descripción.'
            : 'Some words may trigger safety filters. Try adjusting your description.')
        );
        return;
      }

      if (response.data.image_base64) {
        setGeneratedImage(response.data.image_base64);
        setLastPrompt(currentPrompt);
        toast.success(t.imageGenerated);
        if (onImageGenerated) {
          onImageGenerated({ base64: response.data.image_base64, prompt: currentPrompt });
        }
      }
    } catch (error) {
      console.error('Error generating image:', error);
      // Defensa adicional: si el backend (por cualquier razón) escapa con detalles técnicos,
      // los filtramos y mostramos el aviso suave.
      const rawDetail = error?.response?.data?.detail || error?.response?.data?.message || '';
      const lower = String(rawDetail).toLowerCase();
      const moderationHints = ['moderation', 'safety', 'badrequest', 'violence', 'categories', 'image_generation_user_error', 'rejected'];
      if (moderationHints.some((k) => lower.includes(k))) {
        setSoftWarning(
          language === 'es'
            ? 'Algunas palabras pueden activar filtros de seguridad. Intenta ajustar tu descripción.'
            : 'Some words may trigger safety filters. Try adjusting your description.'
        );
      } else {
        toast.error(t.generateError);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    try {
      // Convertir base64 a bytes binarios
      const byteCharacters = atob(generatedImage);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Crear Blob binario real
      const blob = new Blob([byteArray], { type: 'image/png' });
      
      // Crear URL del blob y descargar
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `damyxia_image_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(t.imageDownloaded);
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Error al descargar');
    }
  };

  const handleCopy = async () => {
    if (!generatedImage) return;

    const copyWithTextarea = (text) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.cssText = 'position:fixed;left:-999999px;top:-999999px;opacity:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        return successful;
      } catch (err) {
        document.body.removeChild(textArea);
        return false;
      }
    };

    try {
      const response = await fetch(`data:image/png;base64,${generatedImage}`);
      const blob = await response.blob();
      
      if (navigator.clipboard && navigator.clipboard.write) {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
          setCopied(true);
          toast.success(t.imageCopied);
          setTimeout(() => setCopied(false), 2000);
          return;
        } catch (clipErr) {
        }
      }
      
      const base64Url = `data:image/png;base64,${generatedImage}`;
      let success = false;
      
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(base64Url);
          success = true;
        }
      } catch (textErr) {
      }
      
      if (!success) {
        success = copyWithTextarea(base64Url);
      }
      
      if (success) {
        setCopied(true);
        toast.success(t.urlCopied);
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error(t.copyError);
      }
    } catch (error) {
      console.error('Error copying image:', error);
      toast.error(t.copyError);
    }
  };

  const handleSendToChat = () => {
    if (!generatedImage || !onSendToChat) return;
    onSendToChat({ type: 'image', base64: generatedImage, prompt: lastPrompt || prompt });
    toast.success(t.sentToChat);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-2xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl my-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">{t.title}</h3>
              <p className="text-[10px] text-zinc-400">{t.poweredBy}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors" data-testid="close-image-modal-btn">
            <X className="w-4 h-4 text-zinc-400 hover:text-white" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">{t.describeImage}</label>
            <textarea
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); if (softWarning) setSoftWarning(''); }}
              placeholder={t.placeholder}
              rows={2}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
              data-testid="image-prompt-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">{t.size}</label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-white focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                {sizes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">{t.style}</label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-white focus:outline-none focus:ring-1 focus:ring-purple-500"
              >
                {styles.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <button
            onClick={() => handleGenerate(false)}
            disabled={isGenerating || !prompt.trim()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-zinc-700 disabled:to-zinc-700 text-white font-bold rounded-lg transition-all disabled:cursor-not-allowed shadow-lg shadow-purple-500/25"
            data-testid="generate-image-execute-btn"
          >
            {isGenerating ? (
              <><Loader2 className="w-5 h-5 animate-spin" />{t.generating}</>
            ) : (
              <><Sparkles className="w-5 h-5" />{t.createImage}</>
            )}
          </button>

          {/* CUADRO NEGRO: aviso suave (no rojo) cuando OpenAI activa filtros de seguridad */}
          {softWarning && (
            <div
              role="status"
              aria-live="polite"
              data-testid="image-soft-warning"
              style={{
                background: '#FFF8D6',
                border: '1px solid #E6D28A',
                color: '#5A4E2E',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '13px',
                lineHeight: '1.4',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                marginTop: '4px',
              }}
            >
              <span style={{ fontSize: '16px', lineHeight: '1' }} aria-hidden="true">⚠️</span>
              <span style={{ flex: 1 }}>{softWarning}</span>
              <button
                type="button"
                onClick={() => setSoftWarning('')}
                aria-label={language === 'es' ? 'Cerrar aviso' : 'Dismiss notice'}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#5A4E2E',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '0 4px',
                  lineHeight: '1',
                }}
                data-testid="image-soft-warning-close"
              >
                ×
              </button>
            </div>
          )}

          {generatedImage && (
            <div className="mt-3 space-y-2">
              <div 
                className="relative rounded-lg overflow-hidden border border-zinc-600 cursor-pointer group"
                onClick={() => setFullscreenImage({ base64: generatedImage, prompt: lastPrompt || prompt })}
                title={t.fullscreenView}
              >
                {/* X button to clear/close the image */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setGeneratedImage(null);
                    setLastPrompt('');
                  }}
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-black/70 hover:bg-red-600 text-white transition-colors"
                  title={language === 'es' ? 'Cerrar imagen' : 'Close image'}
                  data-testid="close-generated-image-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                
                <img
                  src={`data:image/png;base64,${generatedImage}`}
                  alt={t.generatedImage}
                  className="w-full h-auto max-h-[500px] object-contain bg-zinc-800"
                  data-testid="generated-image"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                  <div className="flex items-center gap-2 text-white text-sm">
                    <Maximize2 className="w-5 h-5" />
                    <span>{t.fullscreenView}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-1.5">
                <button onClick={handleCopy} className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-medium rounded-md transition-colors border border-zinc-600" data-testid="copy-image-btn">
                  {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                  {copied ? t.copied : t.copy}
                </button>
                <button onClick={handleDownload} className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 bg-green-600 hover:bg-green-500 text-white text-[10px] font-medium rounded-md transition-colors" data-testid="download-image-btn">
                  <Download className="w-3 h-3" />{t.download}
                </button>
                <button onClick={() => setFullscreenImage({ base64: generatedImage, prompt: lastPrompt || prompt })} className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-medium rounded-md transition-colors" data-testid="expand-image-btn">
                  <Maximize2 className="w-3 h-3" />{t.expand}
                </button>
                {onSaveImage && (
                  <button onClick={() => { onSaveImage({ base64: generatedImage, prompt: lastPrompt || prompt }); toast.success(t.imageSaved); }} className="flex-1 flex items-center justify-center gap-1 px-1.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium rounded-md transition-colors" data-testid="save-image-btn">
                    <Save className="w-3 h-3" />{t.save}
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                <button onClick={() => handleGenerate(true)} disabled={isGenerating} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-zinc-700 text-white text-xs font-medium rounded-lg transition-colors disabled:cursor-not-allowed" data-testid="regenerate-image-btn">
                  <RefreshCw className={`w-3.5 h-3.5 ${isGenerating ? 'animate-spin' : ''}`} />{t.variation}
                </button>
                {onSendToChat && (
                  <button onClick={handleSendToChat} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-colors" data-testid="send-to-chat-btn">
                    <MessageSquare className="w-3.5 h-3.5" />{t.analyze}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Fullscreen Viewer */}
      {fullscreenImage && (
        <FullscreenViewer
          imageBase64={fullscreenImage.base64}
          prompt={fullscreenImage.prompt}
          onClose={() => setFullscreenImage(null)}
          onSave={onSaveImage}
          t={t}
        />
      )}
    </div>,
    document.body
  );
};

export default ImageGeneratorModal;
